import { fetchGameAnalysis } from './gameAnalysis';
import { fetchGameRecap } from './gameRecap';

const API_BASE = 'https://statsapi.mlb.com/api/v1';

const parseSlug = (slug = '') => {
  const match = slug.match(
    /^(\d{4}-\d{2}-\d{2})-([a-z0-9]+)-at-([a-z0-9]+)(?:-g(\d+))?$/i
  );

  if (!match) return null;

  return {
    date: match[1],
    away: match[2].toUpperCase(),
    home: match[3].toUpperCase(),
    gameNumber: match[4] ? Number(match[4]) : null
  };
};

const fetchScheduleGameByPk = async (gamePk, signal) => {
  const response = await fetch(
    `${API_BASE}/schedule?gamePk=${encodeURIComponent(gamePk)}&hydrate=team,linescore,decisions`,
    { signal }
  );

  if (!response.ok) return null;

  const data = await response.json();
  return (data.dates || []).flatMap((date) => date.games || [])[0] || null;
};

const findGameFromSlug = async (slug, signal) => {
  const parsed = parseSlug(slug);

  if (!parsed) {
    throw new Error('This game link is not valid.');
  }

  const params = new URLSearchParams({
    sportId: '1',
    teamId: '111',
    date: parsed.date,
    hydrate: 'team,linescore,decisions'
  });

  const response = await fetch(`${API_BASE}/schedule?${params}`, { signal });

  if (!response.ok) {
    throw new Error('The MLB schedule could not be loaded.');
  }

  const data = await response.json();
  const games = (data.dates || []).flatMap((date) => date.games || []);

  const matchingGames = games.filter((game) => {
    const away = game.teams?.away?.team?.abbreviation?.toUpperCase();
    const home = game.teams?.home?.team?.abbreviation?.toUpperCase();

    return away === parsed.away && home === parsed.home;
  });

  if (parsed.gameNumber) {
    return matchingGames.find(
      (game) => Number(game.gameNumber || 1) === parsed.gameNumber
    ) || null;
  }

  return matchingGames[0] || null;
};

const getTeam = (side, feed, scheduleGame, boxScore) => {
  const feedTeam = feed?.gameData?.teams?.[side];
  const scheduleTeam = scheduleGame?.teams?.[side]?.team;
  const boxTeam = boxScore?.teams?.[side]?.team;
  const score =
    feed?.liveData?.linescore?.teams?.[side]?.runs ??
    scheduleGame?.teams?.[side]?.score ??
    boxScore?.teams?.[side]?.teamStats?.batting?.runs ??
    '';

  return {
    name:
      feedTeam?.name ||
      scheduleTeam?.name ||
      boxTeam?.name ||
      boxTeam?.teamName ||
      '',
    abbr:
      feedTeam?.abbreviation ||
      scheduleTeam?.abbreviation ||
      boxTeam?.abbreviation ||
      '',
    score,
    record:
      scheduleGame?.teams?.[side]?.leagueRecord ||
      boxScore?.teams?.[side]?.teamStats?.record ||
      null
  };
};

const normalizeGame = (feed, scheduleGame, analysis, gamePk) => {
  const gameData = feed?.gameData || {};
  const liveData = feed?.liveData || {};
  const boxScore = analysis?.boxScore || liveData.boxscore || {};
  const linescore = analysis?.linescoreData || liveData.linescore || {};
  const officialDate =
    gameData.datetime?.officialDate ||
    scheduleGame?.officialDate ||
    scheduleGame?.gameDate?.slice(0, 10) ||
    '';

  return {
    gamePk: Number(gamePk),
    officialDate,
    date:
      gameData.datetime?.dateTime ||
      scheduleGame?.gameDate ||
      officialDate,
    venue:
      gameData.venue?.name ||
      scheduleGame?.venue?.name ||
      boxScore?.teams?.home?.team?.venue?.name ||
      '',
    status:
      gameData.status?.detailedState ||
      scheduleGame?.status?.detailedState ||
      'Final',
    gameNumber: scheduleGame?.gameNumber || 1,
    doubleHeader: scheduleGame?.doubleHeader || 'N',
    away: getTeam('away', feed, scheduleGame, boxScore),
    home: getTeam('home', feed, scheduleGame, boxScore),
    decisions:
      liveData.decisions ||
      scheduleGame?.decisions ||
      null,
    innings: linescore.innings || [],
    allPlays: analysis?.playByPlay?.allPlays || liveData.plays?.allPlays || [],
    boxScore,
    feed: feed || {
      liveData: {
        boxscore: boxScore,
        linescore,
        decisions: scheduleGame?.decisions || null
      }
    }
  };
};

export async function fetchCompleteGame({ gamePk, slug, signal }) {
  let scheduleGame = null;
  let resolvedGamePk = gamePk ? Number(gamePk) : null;

  if (!resolvedGamePk && slug) {
    scheduleGame = await findGameFromSlug(slug, signal);
    resolvedGamePk = scheduleGame?.gamePk || null;
  }

  if (!resolvedGamePk) {
    throw new Error('This game could not be found on the MLB schedule.');
  }

  const [analysisResult, recapResult, scheduleResult] = await Promise.allSettled([
    fetchGameAnalysis(resolvedGamePk),
    fetchGameRecap(resolvedGamePk),
    scheduleGame
      ? Promise.resolve(scheduleGame)
      : fetchScheduleGameByPk(resolvedGamePk, signal)
  ]);

  if (signal?.aborted) {
    throw new DOMException('Request aborted', 'AbortError');
  }

  const analysis =
    analysisResult.status === 'fulfilled' ? analysisResult.value : null;
  const recap =
    recapResult.status === 'fulfilled' ? recapResult.value : null;

  if (!scheduleGame && scheduleResult.status === 'fulfilled') {
    scheduleGame = scheduleResult.value;
  }

  const hasBoxScore = Boolean(analysis?.boxScore?.teams);
  const hasGameFeed = Boolean(recap?.feed?.gameData);
  const hasSchedule = Boolean(scheduleGame);

  if (!hasBoxScore && !hasGameFeed && !hasSchedule) {
    throw new Error(
      'Game data is temporarily unavailable from MLB. Please try again shortly.'
    );
  }

  return {
    game: normalizeGame(
      recap?.feed || null,
      scheduleGame,
      analysis,
      resolvedGamePk
    ),
    content: recap?.content || null
  };
}